import { useCallback, useMemo, useRef, useEffect } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useSyncSettings } from '@/hooks/admin/useSyncSettings'
import type { APISession } from '@/types/session'
import type { AmendmentDetectionSettings } from '@/api/admin-settings-api'
import type { DataSource } from './useDatasourceState'

export function useAmendmentSettings(selectedDataSource: DataSource) {
  const { toast } = useToast()
  const { data: syncSettings, updateSyncSettings, isPending: isMutating } = useSyncSettings()
  
  const debounceTimersRef = useRef<Record<string, NodeJS.Timeout>>({})

  // Memoize amendment detection settings to prevent excessive recalculation
  const amendmentSettingsCache = useMemo(() => {
    if (!syncSettings?.session_sync) return {}
    
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal'
    const dataSourceSettings = syncSettings.session_sync[dataSourceKey]
    
    if (!dataSourceSettings?.sessions) return {}
    
    const cache: Record<string, AmendmentDetectionSettings> = {}
    Object.entries(dataSourceSettings.sessions).forEach(([session, settings]) => {
      if (settings.amendment_detection) {
        cache[session] = {
          process_new_bills: settings.amendment_detection.process_new_bills ?? true,
          process_version_changes: settings.amendment_detection.process_version_changes ?? true,
          dpi: settings.amendment_detection.dpi ?? 200,
          images_per_page: settings.amendment_detection.images_per_page ?? 2
        }
      }
    })
    
    return cache
  }, [syncSettings, selectedDataSource])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(timer => clearTimeout(timer))
    }
  }, [])

  const handleSyncToggle = (session: APISession, enabled: boolean) => {
    const currentSessionSync = syncSettings?.session_sync || {}
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal'
    
    const currentDataSourceSettings = currentSessionSync[dataSourceKey] || { sessions: {} }
    const currentSessionSettings = currentDataSourceSettings.sessions[session] || {
      sync_enabled: false,
      amendment_detection: {
        process_new_bills: true,
        process_version_changes: true,
        dpi: 200,
        images_per_page: 2
      }
    }

    const updatedSessionSettings = {
      ...currentSessionSettings,
      sync_enabled: enabled
    }

    const updatedDataSourceSettings = {
      ...currentDataSourceSettings,
      sessions: {
        ...currentDataSourceSettings.sessions,
        [session]: updatedSessionSettings
      }
    }

    const updatedSessionSync = {
      ...currentSessionSync,
      [dataSourceKey]: updatedDataSourceSettings
    }

    updateSyncSettings({
      session_sync: updatedSessionSync
    })
    
    toast({
      title: enabled ? `${selectedDataSource} Sync Enabled` : `${selectedDataSource} Sync Disabled`,
      description: `${session} ${selectedDataSource} bill sync has been ${enabled ? 'enabled' : 'disabled'}`
    })
  }

  const handleAmendmentSettingChange = (session: APISession, setting: string, value: boolean | number) => {
    const currentSessionSync = syncSettings?.session_sync || {}
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal'
    
    const currentDataSourceSettings = currentSessionSync[dataSourceKey] || { sessions: {} }
    const existingSessionSettings = currentDataSourceSettings.sessions[session]
    const currentSyncEnabled = existingSessionSettings?.sync_enabled ?? false
    
    const currentAmendmentSettings = existingSessionSettings?.amendment_detection || {
      process_new_bills: true,
      process_version_changes: true,
      dpi: 200,
      images_per_page: 2
    }

    const updatedAmendmentSettings = {
      ...currentAmendmentSettings,
      [setting]: value
    }
    
    const updatedSessionSettings = {
      sync_enabled: currentSyncEnabled,
      amendment_detection: updatedAmendmentSettings
    }

    const updatedDataSourceSettings = {
      ...currentDataSourceSettings,
      sessions: {
        ...currentDataSourceSettings.sessions,
        [session]: updatedSessionSettings
      }
    }

    const updatedSessionSync = {
      ...currentSessionSync,
      [dataSourceKey]: updatedDataSourceSettings
    }

    updateSyncSettings({
      session_sync: updatedSessionSync
    })
    
    toast({
      title: "Amendment Detection Settings Updated",
      description: `${setting.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} has been updated for ${session} ${selectedDataSource}`
    })
  }

  // Simplified debounced handler for numeric input changes
  const handleNumericInputChange = useCallback((session: APISession, setting: 'dpi' | 'images_per_page', value: number) => {
    const sessionKey = `${session}-${setting}`
    
    // Clear existing timer
    if (debounceTimersRef.current[sessionKey]) {
      clearTimeout(debounceTimersRef.current[sessionKey])
    }
    
    // Set new timer for debounced save
    const newTimer = setTimeout(() => {
      handleAmendmentSettingChange(session, setting, value)
      delete debounceTimersRef.current[sessionKey]
    }, 1000)
    
    debounceTimersRef.current[sessionKey] = newTimer
  }, [handleAmendmentSettingChange])

  const isSessionSyncEnabled = (session: APISession): boolean => {
    const dataSourceKey = selectedDataSource.toLowerCase() as 'maryland' | 'federal'
    return syncSettings?.session_sync?.[dataSourceKey]?.sessions?.[session]?.sync_enabled || false
  }

  const getAmendmentDetectionSettings = useCallback((session: APISession): AmendmentDetectionSettings => {
    return amendmentSettingsCache[session] || {
      process_new_bills: true,
      process_version_changes: true,
      dpi: 200,
      images_per_page: 2
    }
  }, [amendmentSettingsCache])

  const isAmendmentDetectionEnabled = (session: APISession): boolean => {
    const settings = getAmendmentDetectionSettings(session)
    return isSessionSyncEnabled(session) && (settings.process_new_bills || settings.process_version_changes)
  }

  return {
    syncSettings,
    isMutating,
    handleSyncToggle,
    handleAmendmentSettingChange,
    handleNumericInputChange,
    isSessionSyncEnabled,
    getAmendmentDetectionSettings,
    isAmendmentDetectionEnabled
  }
}